import React, { useMemo } from 'react';

import { Button } from '@components/Button';
import { Layout } from '@components/Layout';
import { PrintableContainer } from '@components/Printable';
import {
  TrackingHeader,
  TrackingItem,
} from '@components/utilities/CargoTracking/CargoTrackingResult';
import { useTrackingDataList } from '@hooks/queries/useTrackingDataList';
import { useSearch } from '@hooks/useSearch';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableContainer from '@material-ui/core/TableContainer';
import { TrackingData } from '@models/TrackingData';
import { cargoTrackingResultQuery } from '@pages/utilities/CargoTracking/helpers';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { toJST } from '@utils/types';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    title: '配送情報一覧',
    print: '印刷',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  background: {
    backgroundColor: theme.palette.background.paper,
  },
  buttonContainer: {
    ...common.flexCenter,
    marginTop: 72,
    '@media print': {
      display: 'none',
    },
  },
  tableContainer: {
    boxShadow: 'none',
  },
}));

export const PrintCargoTrackingOverview: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const dateString = useMemo(() => toJST(new Date()), []);

  const {
    search: { trackNos },
  } = useSearch(cargoTrackingResultQuery);

  const trackingList = useTrackingDataList(trackNos);

  const trackNoToData = useMemo(() => {
    const map = new Map<string, TrackingData>();
    if (trackingList.data === undefined) return map;

    for (const data of trackingList.data) {
      map.set(data.track_no, data);
    }
    return map;
  }, [trackingList.data]);

  return (
    <Layout
      title={strings.title}
      hideHeader
      hideFooter
      hideCookiePolicy
      loading={trackingList.isLoading}
      errors={[trackingList.error]}
      classes={{
        background: classes.background,
      }}
    >
      {() => (
        <PrintableContainer>
          <div className={clsx(classes.flexInline, classes.middleText, classes.textGray800)}>
            {strings.title}
            <span style={{ width: '2rem' }} />
            {dateString}
          </div>

          <div style={{ height: 30 }} />

          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table aria-label="collapsible table">
              <TrackingHeader variant="printable" />
              <TableBody>
                {trackNos.map((trackNo, i) => (
                  <TrackingItem
                    key={i}
                    index={i}
                    variant="printable"
                    trackNo={trackNo}
                    trackingData={trackNoToData.get(trackNo)!}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <div className={classes.buttonContainer}>
            <Button color="printable" text={strings.print} size="small" onClick={window.print} />
          </div>
        </PrintableContainer>
      )}
    </Layout>
  );
};
